/**
 * engineNormal_Helper.ts - Helper functions for EngineNormal
 * Port of C# EngineNormal_Helper.cs
 * Pure ES Module version
 */

import { JsonObject } from '../../app/json/jsonValue.js';
import { JsonMergeUtil } from '../../base/engine/jsonMergeUtil.js';
import { LogLevel, Logger } from '../../../../Arshu/dist/logger/logger.js';
import type { EngineNormal } from './engineNormal.js';
import type { TemplateOperation } from '../../model/templateOperation.js';
import type { InheritanceOperation } from '../../model/inheritanceOperation.js';

/**
 * Function signature for the bound getTemplate-with-json helper.
 */
export type GetTemplateWithJsonFn = (
    appSiteArg: string,
    currentTemplateName: string,
    placeholderName: string,
    appViewArg: string | null,
    flowStackArg: string[] | null,
    configJsonArg: JsonObject | null
) => { html: string | null; json: JsonObject | null; resolvedTemplateName: string | null; resolvedAppSite: string | null };

/**
 * Function signature for the bound mergeJsonIntoTemplate helper.
 */
export type MergeJsonIntoTemplateFn = (
    html: string,
    json: JsonObject | null,
    placeholderName: string,
    resolvedTemplateName: string | null,
    resolvedAppSite: string | null,
    ops: TemplateOperation[],
    ctxJson: JsonObject | null,
    cfgJson: JsonObject | null
) => string;

/**
 * Bundles the two bound callback functions used by EngineNormal merge
 * helpers (`executeMergeIterativeExpansion`, `mergeTemplateSlots`, etc.).
 * Bundling is required because the inline arrow-function types contain `=>`,
 * which the polyglot CompareStructure arity audit miscounts as `>` decrements,
 * inflating the apparent param count past the 18-param ceiling.
 */
export interface MergeBoundCallbacks {
    getTemplateWithJsonBound: GetTemplateWithJsonFn;
    mergeJsonIntoTemplateBound: MergeJsonIntoTemplateFn;
}

/**
 * Convert milliseconds to nanoseconds
 */
export function msToNanos(ms: number): number {
    return Math.round(ms * 1_000_000);
}

/**
 * Create flow stack for inheritance resolution
 */
export function createFlowStack(appFile: string): string[] {
    return [appFile];
}

export function getRecursionDepthError(engine: EngineNormal, recursionDepth: number, templateKey: string): string | null {
    if (recursionDepth <= engine._loader!.maxRecursionDepth) {
        return null;
    }

    const errorMsg = `[ERROR] Maximum recursion depth (${engine._loader!.maxRecursionDepth}) exceeded for template ${templateKey}. Possible circular template reference.`;
    Logger.error(errorMsg, 'EngineNormal');
    return errorMsg;
}

/**
 * Collects the distinct slot markers still present in HTML that failed to converge, so a
 * non-stabilizing merge names the templates involved instead of dumping the whole document.
 * Scans the same {{# / {{!# forms processTemplateSlots recognizes, capped so a large document
 * cannot turn the error message into another payload.
 */
export function collectUnresolvedMarkers(html: string): string {
    const maxReported = 10;
    const names: string[] = [];
    let searchPos = 0;

    while (searchPos < html.length && names.length < maxReported) {
        let openStart = html.indexOf('{{#', searchPos);
        const openStartSkip = html.indexOf('{{!#', searchPos);

        const isSkipMarked = openStartSkip !== -1 && (openStart === -1 || openStartSkip < openStart);
        if (isSkipMarked) {
            openStart = openStartSkip;
        }

        if (openStart === -1) break;

        const nameOffset = isSkipMarked ? 4 : 3;
        const openEnd = html.indexOf('}}', openStart + nameOffset);
        if (openEnd === -1) break;

        const templateName = html.substring(openStart + nameOffset, openEnd).trim();
        if (templateName.length > 0 && !names.includes(templateName)) {
            names.push(templateName);
        }

        searchPos = openEnd + 2;
    }

    return names.length === 0 ? '(none)' : names.join(', ');
}

/**
 * Get main template from loader
 */
export function getMainTemplate(engine: EngineNormal, appSite: string, appFile: string, appView: string | null, flowStack: string[], configJson: JsonObject | null = null, clientMode: boolean = false): { contentHtml: string | null; mainJson: JsonObject | null; resolvedAppSite: string } {
    const startGetTemplate = performance.now();
    const { html: contentHtml, json: mainJson, resolvedAppSite } = engine._loader!.getTemplateWithView(appSite, appFile, appFile, appView, flowStack, configJson, clientMode);
    engine._getTemplateNanos += msToNanos(performance.now() - startGetTemplate);
    engine._getTemplateCount++;

    return { contentHtml, mainJson, resolvedAppSite };
}

/**
 * Merge main template with JSON data
 */
export function mergeMainTemplateJson(engine: EngineNormal, contentHtml: string, mainJson: JsonObject | null, templateKey: string, appFile: string, operations: TemplateOperation[], contextJson: JsonObject | null, configJson: JsonObject | null): string {
    if (mainJson === null) {
        return contentHtml;
    }

    if (Logger.getLogLevel() <= LogLevel.DEBUG) {
        Logger.debug(`Merging main template with JSON`, 'EngineNormal');
    }
    const startJsonMerge = performance.now();
    const merged = JsonMergeUtil.mergeTemplateWithJson(
        contentHtml,
        mainJson,
        templateKey,
        appFile,
        operations,
        engine._loader!.maxArrayOperationItems,
        engine._loader!.defaultReplaceHtml,
        engine._loader!.defaultSkipJson,
        null,  // arrayMetadata (Normal engine has no preprocessed metadata)
        null,  // jsonKeyMetadata
        contextJson,
        configJson
    );
    engine._jsonMergeNanos += msToNanos(performance.now() - startJsonMerge);
    engine._jsonMergeCount++;
    if (Logger.getLogLevel() <= LogLevel.DEBUG) {
        Logger.debug(`After main JSON merge: ${merged.length} chars`, 'EngineNormal');
    }

    return merged;
}

/**
 * Build operations metadata
 */
export function buildOperationsMetadata(genOperationJson: boolean, appSite: string, appFile: string, appView: string | null, templateKey: string, operations: TemplateOperation[], inheritanceOps: InheritanceOperation[]): { appSite: string; appFile: string; appView: string; templateName: string; operations: TemplateOperation[]; hasInheritance: boolean; inheritanceOperations: InheritanceOperation[] } | null {
    if (!genOperationJson) {
        return null;
    }

    return {
        appSite: appSite,
        appFile: appFile,
        appView: appView || '',
        templateName: templateKey,
        operations: operations,
        hasInheritance: inheritanceOps.length > 0,
        inheritanceOperations: inheritanceOps
    };
}

/**
 * Accumulate dynamic data timing from loader
 */
export function accumulateDynamicDataTimingFromLoader(engine: EngineNormal): void {
    const { nanos: dynamicDataNanos, count: dynamicDataCount } = engine._loader!.getDynamicDataTiming();
    engine.accumulateDynamicDataTiming(dynamicDataNanos, dynamicDataCount);
}

/**
 * Accumulate dynamic view timing from loader
 */
export function accumulateDynamicViewTimingFromLoader(engine: EngineNormal): void {
    const { nanos: dynamicViewNanos, count: dynamicViewCount } = engine._loader!.getDynamicViewTiming();
    engine.accumulateDynamicViewTiming(dynamicViewNanos, dynamicViewCount);
}

/**
 * Accumulate dynamic action timing from loader
 */
export function accumulateDynamicActionTimingFromLoader(engine: EngineNormal): void {
    const { nanos: dynamicActionNanos, count: dynamicActionCount } = engine._loader!.getDynamicActionTiming();
    engine.accumulateDynamicActionTiming(dynamicActionNanos, dynamicActionCount);
}

export function accumulateDynamicClientDataTimingFromLoader(engine: EngineNormal): void {
    const { nanos: dynamicClientDataNanos, count: dynamicClientDataCount } = engine._loader!.getDynamicClientDataTiming();
    engine.accumulateDynamicClientDataTiming(dynamicClientDataNanos, dynamicClientDataCount);
}

/**
 * Gets template HTML and JSON without merging - merge is done AFTER DynamicViewHandlers
 * Returns the HTML content and the actual template name that was loaded (after AppView fallback)
 */
export function getTemplateWithJson(engine: EngineNormal, appSite: string, currentTemplateName: string, placeholderName: string, appView: string | null, flowStack: string[] | null = null, configJson: JsonObject | null = null, clientMode: boolean = false): { html: string | null; json: JsonObject | null; resolvedTemplateName: string | null; resolvedAppSite: string | null } {
    // Get HTML template and JSON (includes AppView fallback and SearchAppSites logic)
    const startGetTemplate = performance.now();
    const { html, json, resolvedTemplateName, resolvedAppSite } = engine._loader!.getTemplateWithView(appSite, currentTemplateName, placeholderName, appView, flowStack, configJson, clientMode);
    engine._getTemplateNanos += msToNanos(performance.now() - startGetTemplate);
    engine._getTemplateCount++;
    // Allow empty strings (skip markers) but not null/undefined
    if (html == null) return { html: null, json: null, resolvedTemplateName: null, resolvedAppSite: null };

    if (Logger.getLogLevel() <= LogLevel.DEBUG) {
        Logger.debug(`GetTemplateWithJson: placeholder=${placeholderName}, currentTemplate=${currentTemplateName}, resolvedTemplate=${resolvedTemplateName}, html size=${html ? html.length : 0}`, 'EngineNormal');
    }

    return { html, json, resolvedTemplateName, resolvedAppSite };
}

/**
 * Merges JSON into template HTML - called AFTER DynamicViewHandlers
 */
export function mergeJsonIntoTemplate(engine: EngineNormal, html: string, json: JsonObject | null, placeholderName: string, resolvedTemplateName: string | null, resolvedAppSite: string | null, operations: TemplateOperation[], contextJson: JsonObject | null, configJson: JsonObject | null = null): string {
    if (!html || html === '') {
        return html;
    }

    // Determine which default HTML to use for JSON merging based on template type
    // Skip markers use DefaultSkipHtml, missing/not found templates use DefaultReplaceHtml
    const defaultHtmlForMerge = (resolvedTemplateName === engine._loader!.defaultSkipTemplateName)
        ? engine._loader!.defaultSkipHtml
        : engine._loader!.defaultReplaceHtml;

    const originalSize = html.length;
    // Calculate resolved templateKey using resolvedTemplateName (not requested placeholder name)
    const resolvedTemplateKey = `${resolvedAppSite}_${resolvedTemplateName || placeholderName}`;
    // Use empty JsonObject if json is null (to replace missing placeholders with default HTML)
    const jsonToMerge = json !== null ? json : new JsonObject();
    const startJsonMerge = performance.now();
    const mergedHtml = JsonMergeUtil.mergeTemplateWithJson(
        html,
        jsonToMerge,
        resolvedTemplateKey,
        resolvedTemplateName || placeholderName,
        operations,
        engine._loader!.maxArrayOperationItems,
        defaultHtmlForMerge,
        engine._loader!.defaultSkipJson,
        null,  // arrayMetadata (Normal engine has no preprocessed metadata)
        null,  // jsonKeyMetadata
        contextJson,
        configJson
    );
    engine._jsonMergeNanos += msToNanos(performance.now() - startJsonMerge);
    engine._jsonMergeCount++;
    if (Logger.getLogLevel() <= LogLevel.DEBUG) {
        Logger.debug(`After JSON merge for ${placeholderName} (resolvedTemplate=${resolvedTemplateName}): size ${originalSize} -> ${mergedHtml.length}`, 'EngineNormal');
    }

    return mergedHtml;
}
